import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useI18n, useRootContext } from '@jarvis/react-portal-addons';
import NotificationsWidget from '../NotificationsWidget';
import PrinterCarousel from '../PrinterCarousel';
import useUsersApiCall from '../../../hooks/useUsersApiCall';
import useOpenStoreForHpApp from '../../../hooks/useOpenStoreForHpApp';
import {
  Button,
  Container,
  MessageContainer,
  NotificationsWidgetContainer,
  PadArea,
  PrinterCarouselContainer,
  Title,
  TitleAndNotificationsWrapper,
} from './styles';

const Banner = ({ 'data-testid': dataTestId, deviceCache }) => {
  const [notificationsExpanded, setNotificationsExpanded] = useState(false);
  const { consolidatedPortalPrinterCarousel, consolidatedPortalGetAppButton } = useFlags();
  const { stack, shell: { authProvider, store } } = useRootContext();
  const { t } = useI18n();
  const { openStoreForHpApp, hpAppUrl } = useOpenStoreForHpApp();

  const notificationsCollapsed = !notificationsExpanded;

  const userInfosCall = useUsersApiCall({
    authProvider,
    stack,
    store,
  });

  const { data: userInfos } = userInfosCall;

  const {
    pending: isLoadingDevices,
    data: devices,
  } = deviceCache;

  const accountHasDevice = useMemo(() => devices && devices.length > 0, [devices]);
  const showNotificationsAndCarousel = useMemo(
    () => !isLoadingDevices && accountHasDevice,
    [accountHasDevice, isLoadingDevices],
  );

  return (
    <Container
      data-testid={dataTestId} accountHasDevice={isLoadingDevices || accountHasDevice}
      expanded={notificationsExpanded}
    >
      <TitleAndNotificationsWrapper accountHasDevice={accountHasDevice}>
        {accountHasDevice && (
          <NotificationsWidgetContainer
            data-testid="smart-notification-widget-mfe"
            collapsed={notificationsCollapsed} expanded={notificationsExpanded}
          >
            <NotificationsWidget
              stack={stack} properties={{
                onCollapse: () => {
                  setNotificationsExpanded(false);
                },
                onExpand: () => {
                  setNotificationsExpanded(true);
                },
              }}
            />
          </NotificationsWidgetContainer>
        )}

        <PadArea />

        <Title
          accountHasDevice={accountHasDevice}
          className="title-regular"
          data-testid="user-name"
          hide={notificationsExpanded}
        >
          {t('dashboard.header.welcome.option1', {
            name: userInfos?.data?.givenName,
            interpolation: { escapeValue: false },
          })}
        </Title>
      </TitleAndNotificationsWrapper>

      {showNotificationsAndCarousel && consolidatedPortalPrinterCarousel && (
        <PrinterCarouselContainer data-testid="printer-carousel-container">
          <PrinterCarousel devices={devices} stack={stack} />
        </PrinterCarouselContainer>
      )}

      {!isLoadingDevices && !accountHasDevice && (
        <MessageContainer>
          <p data-testid="connect-printer-message" className="subtitle-large">{t('dashboard.header.connectPrinter')}</p>
          {consolidatedPortalGetAppButton && hpAppUrl
            && (
            <Button
              data-testid="get-the-app-button"
              appearance="primary"
              onClick={openStoreForHpApp}
            >
              {t('dashboard.header.getTheApp')}
            </Button>
            )}
        </MessageContainer>
      )}
    </Container>
  );
};

Banner.defaultProps = {
  'data-testid': null,
};

Banner.propTypes = {
  'data-testid': PropTypes.string,
  deviceCache: PropTypes.shape({
    pending: PropTypes.bool,
    data: PropTypes.arrayOf(PropTypes.object),
    error: PropTypes.shape({
      message: PropTypes.string,
      code: PropTypes.number,
    }),
    isFetching: PropTypes.bool,
    forceFetch: PropTypes.func,
    makeApiCall: PropTypes.func,
    success: PropTypes.bool,
  }).isRequired,
};

export default Banner;
